import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react"
import { getSystemConfig, normalizeSystemAssetUrl, type SystemConfigDto } from "shared/api/systemConfigService"

export interface SystemBranding extends SystemConfigDto {
  logoUrl: string
  faviconUrl: string
  systemUrl: string
  supportEmail: string
  supportPhone: string
  env: string
}

const defaultBranding: SystemBranding = {
  systemName: "E-PTW Platform",
  companyName: "Electronic Permit to Work System",
  logoUrl: "",
  faviconUrl: "",
  systemUrl: "",
  supportEmail: "",
  supportPhone: "",
  env: "",
}

type SystemBrandingContextValue = {
  branding: SystemBranding
  isLoading: boolean
  refreshBranding: () => Promise<void>
  applyBranding: (config: SystemConfigDto) => void
}

const SystemBrandingContext = createContext<SystemBrandingContextValue | null>(null)

function normalizeBranding(config?: Partial<SystemConfigDto> | null): SystemBranding {
  return {
    systemName: config?.systemName?.trim() || defaultBranding.systemName,
    companyName: config?.companyName?.trim() || defaultBranding.companyName,
    logoUrl: normalizeSystemAssetUrl(config?.logoUrl),
    faviconUrl: normalizeSystemAssetUrl(config?.faviconUrl),
    systemUrl: config?.systemUrl?.trim() || "",
    supportEmail: config?.supportEmail?.trim() || "",
    supportPhone: config?.supportPhone?.trim() || "",
    env: config?.env?.trim() || "",
  }
}

function updateFavicon(url: string) {
  if (typeof document === "undefined") return

  const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement | null
  if (!favicon) return

  favicon.href = url || `${process.env.PUBLIC_URL || ""}/favicon.ico`
}

export function SystemBrandingProvider({ children }: PropsWithChildren) {
  const [branding, setBranding] = useState<SystemBranding>(defaultBranding)
  const [isLoading, setIsLoading] = useState(true)

  const applyBranding = useCallback((config: SystemConfigDto) => {
    setBranding(normalizeBranding(config))
  }, [])

  const refreshBranding = useCallback(async () => {
    setIsLoading(true)
    try {
      const config = await getSystemConfig()
      setBranding(normalizeBranding(config))
    } catch (error) {
      console.error("[system-branding] failed to load system configuration", error)
      setBranding(defaultBranding)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshBranding()
  }, [refreshBranding])

  useEffect(() => {
    document.title = branding.systemName
    updateFavicon(branding.faviconUrl)
  }, [branding.faviconUrl, branding.systemName])

  const value = useMemo<SystemBrandingContextValue>(() => ({
    branding,
    isLoading,
    refreshBranding,
    applyBranding,
  }), [applyBranding, branding, isLoading, refreshBranding])

  return (
    <SystemBrandingContext.Provider value={value}>
      {children}
    </SystemBrandingContext.Provider>
  )
}

export function useSystemBranding() {
  const context = useContext(SystemBrandingContext)
  if (!context) {
    throw new Error("useSystemBranding must be used within SystemBrandingProvider")
  }
  return context
}
